import { Table, type TableProps, Tag } from 'antd';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type UserGroupListDataFragment } from '../../../api/fragments/UserGroupListData';
import { useListUserGroupsQuery } from '../../../api/queries/listUserGroups';
import { UserGroupSortingField } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import UserGroupEmptyListResult from './UserGroupEmptyListResult';

const getSortField = makeGetSortingRule((field): UserGroupSortingField => {
    switch (field) {
        case 'displayName':
            return UserGroupSortingField.DisplayName;

        case 'userCount':
            return UserGroupSortingField.UserCount;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});

const UserGroupList = () => {
    const { t } = useTranslation('userGroupList');

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.UserGroup);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, filter, sort } = state;
    const { loading, error, data } = useListUserGroupsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { ...filter, companyId: company?.id },
            sort: pick(['field', 'order'], sort),
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
        },
    });

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.page?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.page?.count || 0;

    const searchBox = useSearchInDropDown();

    const navigate = useNavigate();

    const onChange = useCallback<TableProps<UserGroupListDataFragment>['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({ type: 'setSort', sortBy: getSortField(sorter) });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            displayName: !filters.displayName ? null : (filters.displayName[0] as string),
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: UserGroupListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/accesses/userGroups/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('userGroupList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['company', 'displayName']}
                    title={t('userGroupList:columns.company')}
                />
            )}
            <Table.Column<UserGroupListDataFragment>
                key="displayName"
                dataIndex="displayName"
                filterDropdown={searchBox.render('description')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={sort.field === UserGroupSortingField.DisplayName ? sort.orderValue : undefined}
                title={t('userGroupList:columns.name')}
                sorter
            />
            <Table.Column
                key="superiorUserGroups"
                dataIndex="superiorUserGroups"
                render={(value: UserGroupListDataFragment['superiorUserGroups']) =>
                    value.map(superior => <Tag key={superior.id}>{superior.displayName}</Tag>)
                }
                title={t('userGroupList:columns.parentUserGroups')}
            />
            <Table.Column
                key="isParallel"
                align="center"
                dataIndex="isParallel"
                render={value => renderBooleanIcon(value, true)}
                title={t('userGroupList:columns.parallel')}
                width={120}
            />
            <Table.Column
                key="userCount"
                dataIndex="userCount"
                sortOrder={sort.field === UserGroupSortingField.UserCount ? sort.orderValue : undefined}
                title={t('userGroupList:columns.userCount')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default UserGroupList;
